import type { ReactNode, SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";

export type SelectSize = "sm" | "md" | "lg";

export type SelectOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
};

export type SelectProps =
  & Omit<SelectHTMLAttributes<HTMLSelectElement>, "size">
  & {
    size?: SelectSize;
    options?: SelectOption[];
    icon?: ReactNode;
    wrapperClassName?: string;
    chevronClassName?: string;
  };

const sizeClasses: Record<SelectSize, string> = {
  sm: "pl-3 pr-9 py-1.5 text-sm",
  md: "pl-3.5 pr-10 py-2 text-sm",
  lg: "pl-4 pr-11 py-2.5 text-base",
};

const iconPaddingClasses: Record<SelectSize, string> = {
  sm: "pl-8",
  md: "pl-9",
  lg: "pl-10",
};

const chevronRightClasses: Record<SelectSize, string> = {
  sm: "right-2.5",
  md: "right-3",
  lg: "right-3.5",
};

export const Select = ({
  size = "md",
  options,
  children,
  icon,
  className = "",
  wrapperClassName = "",
  chevronClassName = "",
  disabled,
  ...rest
}: SelectProps) => (
  <div className={`relative flex items-center ${wrapperClassName}`}>
    {icon && (
      <div className="absolute left-3 pointer-events-none text-gray-400 flex items-center justify-center">
        {icon}
      </div>
    )}
    <select
      disabled={disabled}
      className={`appearance-none bg-[#1a1a1a] border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&>option]:bg-[#1f1f23] [&>option]:text-white ${
        sizeClasses[size]
      } ${icon ? iconPaddingClasses[size] : ""} ${className}`}
      {...rest}
    >
      {options
        ? options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))
        : children}
    </select>
    <ChevronDown
      className={`absolute ${
        chevronRightClasses[size]
      } w-4 h-4 text-gray-400 pointer-events-none ${chevronClassName}`}
    />
  </div>
);
